import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Download, X } from "lucide-react";
import type { ChatMessageAttachment } from "../types";

interface MediaLightboxProps {
  /** Cała seria, po której da się przechodzić strzałkami (jak na WhatsAppie). */
  items: ChatMessageAttachment[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /** Podpis pod paskiem górnym — kto i kiedy wysłał. */
  caption?: string;
  /** „Pokaż w rozmowie" — nieobecne, gdy podgląd otwarto już z wiadomości. */
  onJumpToMessage?: () => void;
}

function isVideo(contentType: string | undefined): boolean {
  return !!contentType && contentType.startsWith("video/");
}

function isAudio(contentType: string | undefined): boolean {
  return !!contentType && contentType.startsWith("audio/");
}

/**
 * Pełnoekranowy podgląd multimediów rozmowy. Renderowany portalem do <body>,
 * bo panel galerii i dymki wiadomości siedzą w kontenerach z `overflow: hidden`
 * — bez portalu podgląd zostałby przycięty do listy.
 */
export function MediaLightbox({
  items,
  index,
  onIndexChange,
  onClose,
  caption,
  onJumpToMessage,
}: MediaLightboxProps) {
  const item = items[index];
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  const go = useCallback(
    (delta: number) => {
      const next = index + delta;
      if (next >= 0 && next < items.length) onIndexChange(next);
    },
    [index, items.length, onIndexChange],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  if (!item) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[70] bg-black/90 flex flex-col"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex items-center gap-3 px-4 py-2 text-white/90 text-xs flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="truncate flex-1" title={item.original_filename}>
          {item.original_filename}
          {caption && <span className="text-white/60"> · {caption}</span>}
        </span>
        {items.length > 1 && (
          <span className="text-white/60 flex-shrink-0">
            {index + 1} / {items.length}
          </span>
        )}
        {onJumpToMessage && (
          <button
            type="button"
            onClick={() => {
              onJumpToMessage();
              onClose();
            }}
            className="rounded px-2 py-1 hover:bg-white/10 flex-shrink-0"
          >
            Pokaż w rozmowie
          </button>
        )}
        <a
          href={item.url ?? "#"}
          download={item.original_filename}
          target="_blank"
          rel="noreferrer"
          title="Pobierz"
          className="rounded p-1.5 hover:bg-white/10 flex-shrink-0"
        >
          <Download size={16} />
        </a>
        <button
          type="button"
          onClick={onClose}
          title="Zamknij (Esc)"
          className="rounded p-1.5 hover:bg-white/10 flex-shrink-0"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 min-h-0 flex items-center justify-center px-2 pb-4">
        {hasPrev && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            title="Poprzednie (←)"
            className="rounded-full p-2 text-white/80 hover:bg-white/10 flex-shrink-0"
          >
            <ChevronLeft size={28} />
          </button>
        )}
        <div
          className="flex-1 h-full flex items-center justify-center min-w-0"
          onClick={(e) => e.stopPropagation()}
        >
          {isVideo(item.content_type) ? (
            <video src={item.url ?? ""} controls className="max-h-full max-w-full" />
          ) : isAudio(item.content_type) ? (
            <audio src={item.url ?? ""} controls className="w-full max-w-md" />
          ) : (
            <img
              src={item.url ?? ""}
              alt={item.original_filename}
              className="max-h-full max-w-full object-contain"
            />
          )}
        </div>
        {hasNext && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            title="Następne (→)"
            className="rounded-full p-2 text-white/80 hover:bg-white/10 flex-shrink-0"
          >
            <ChevronRight size={28} />
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
